/**
 * The library's icon set: a small, curated collection of 24x24 stroked paths.
 *
 * Deliberately not an icon package. The component library needs exactly the
 * affordance glyphs its own controls draw — a chevron, a check, a close, a sort
 * caret — and shipping a 1500-icon dependency to get them would be the kind of
 * decoration CLAUDE.md §11 rules out. Apps bring their own icon set for module
 * navigation.
 */

export const ICON_PATHS = {
  check: 'M20 6 9 17l-5-5',
  chevronDown: 'm6 9 6 6 6-6',
  chevronUp: 'm18 15-6-6-6 6',
  chevronLeft: 'm15 18-6-6 6-6',
  chevronRight: 'm9 18 6-6-6-6',
  chevronsLeft: 'm11 17-5-5 5-5M18 17l-5-5 5-5',
  chevronsRight: 'm13 17 5-5-5-5M6 17l5-5-5-5',
  close: 'M18 6 6 18M6 6l12 12',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  sortAsc: 'M12 19V5M5 12l7-7 7 7',
  sortDesc: 'M12 5v14M19 12l-7 7-7-7',
  sortNone: 'M8 9l4-4 4 4M16 15l-4 4-4-4',
  columns: 'M4 4h16v16H4zM10 4v16M16 4v16',
  filter: 'M3 5h18l-7 8v6l-4 2v-8L3 5Z',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  arrowUp: 'M12 19V5M5 12l7-7 7 7',
  arrowDown: 'M12 5v14M19 12l-7 7-7-7',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  moreHorizontal: 'M6 12h.01M12 12h.01M18 12h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 11v5M12 8h.01',
  alert: 'M12 8v5M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  success: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8.5 12.5l2.5 2.5 4.5-5',
  danger: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v6M12 16h.01',
  download: 'M12 3v12M7 11l5 5 5-5M4 21h16',
  refresh: 'M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  drag: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  inbox: 'M4 13h4l2 3h4l2-3h4M4 13 6.4 5.2A2 2 0 0 1 8.3 4h7.4a2 2 0 0 1 1.9 1.2L20 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5Z',
  command: 'M15 6a3 3 0 1 1 3 3h-3V6ZM9 6a3 3 0 1 0-3 3h3V6ZM9 18a3 3 0 1 1-3-3h3v3ZM15 18a3 3 0 1 0 3-3h-3v3ZM9 9h6v6H9z',
} as const

export type IconName = keyof typeof ICON_PATHS

/**
 * Icons whose glyph points along the reading axis, and which therefore have to
 * mirror when the document is `dir="rtl"` (CLAUDE.md §11).
 *
 * This is an explicit list rather than a blanket `transform: scaleX(-1)` on
 * every icon, because most glyphs must *not* flip. A checkmark mirrored is a
 * checkmark drawn wrong; a sort caret points up and down, not along the reading
 * direction; a spinner has no direction at all. `arrowUp`/`arrowDown` and
 * `sortAsc`/`sortDesc` are absent from this set on purpose.
 */
export const DIRECTIONAL_ICONS: ReadonlySet<IconName> = new Set<IconName>([
  'chevronLeft',
  'chevronRight',
  'chevronsLeft',
  'chevronsRight',
  'arrowRight',
  // "Opens elsewhere" — the arrow leaves the box towards the reading end.
  'external',
])

/** True when {@link EIcon} should mirror this glyph under `dir="rtl"`. */
export function isDirectionalIcon(name: IconName): boolean {
  return DIRECTIONAL_ICONS.has(name)
}
